import React, { useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Mic, FileText, Sparkles, ChevronDown, MessageCircle, PenLine, Wrench, NotebookPen } from 'lucide-react';
import VoiceOverlay from '@/components/VoiceMode/VoiceOverlay';
import CapabilityInfoButton from './CapabilityInfoButton';

const WRITING_KEYWORDS = [
  'write', 'report', 'assessment', 'evaluation', 'summary', 'letter',
  'document', 'case presentation', 'progress report', 'treatment plan',
  'clinical', 'draft', 'compose', 'generate',
];

type AiMode = 'conversation' | 'note_writing' | 'tool_use' | 'work_product_writing';

const MODE_CONFIG: Record<AiMode, { label: string; icon: React.ReactNode; color: string }> = {
  conversation: {
    label: 'Conversation',
    icon: <MessageCircle size={12} />,
    color: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700',
  },
  note_writing: {
    label: 'Note Writing',
    icon: <PenLine size={12} />,
    color: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700',
  },
  tool_use: {
    label: 'Using Tools',
    icon: <Wrench size={12} />,
    color: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700',
  },
  work_product_writing: {
    label: 'Project Writing',
    icon: <NotebookPen size={12} />,
    color: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-700',
  },
};

const SELECTABLE_MODES: AiMode[] = ['conversation', 'note_writing', 'tool_use'];

/**
 * Small "i" button shown in the input area so users can always
 * discover the AI's capabilities without scrolling back to the first message.
 */
function InfoButton() {
  return (
    <div className="flex-shrink-0 self-center mb-1">
      <CapabilityInfoButton />
    </div>
  );
}

/**
 * Mode indicator badge with click-to-change dropdown.
 * Updates automatically when the AI responds (auto-anticipation).
 */
export function ModeBadge({
  aiMode,
  modeOverride,
  onModeOverride,
}: {
  aiMode: string;
  modeOverride: string | null;
  onModeOverride: (mode: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        !menuRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    if (open) {
      const button = dropdownRef.current?.querySelector('button');
      if (button) {
        const rect = button.getBoundingClientRect();
        setMenuPosition({ top: rect.bottom + 4, left: rect.left });
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const displayMode = (modeOverride || aiMode) as AiMode;
  const config = MODE_CONFIG[displayMode] || MODE_CONFIG.conversation;
  const isOverridden = modeOverride !== null;

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border transition-all ${config.color} hover:opacity-80`}
        title={isOverridden ? `Mode locked to ${config.label} (click to change)` : `AI mode: ${config.label} (click to change)`}
      >
        {config.icon}
        <span>{config.label}</span>
        {isOverridden && (
          <span className="ml-0.5 w-1.5 h-1.5 rounded-full bg-current opacity-70" title="Manually set" />
        )}
        <ChevronDown size={10} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          className="fixed w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-[1400] py-1"
          style={menuPosition}
        >
          <button
            onClick={() => { onModeOverride(null); setOpen(false); }}
            className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
              !isOverridden ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'
            }`}
          >
            <Sparkles size={12} />
            <span>Auto (AI decides)</span>
          </button>
          <div className="border-t border-gray-100 dark:border-gray-700 my-0.5" />
          {SELECTABLE_MODES.map((mode) => {
            const mc = MODE_CONFIG[mode];
            const isActive = isOverridden && modeOverride === mode;
            return (
              <button
                key={mode}
                onClick={() => { onModeOverride(mode); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  isActive ? 'font-semibold' : ''
                } text-gray-700 dark:text-gray-300`}
              >
                {mc.icon}
                <span>{mc.label}</span>
                {isActive && <span className="ml-auto text-blue-500">&#10003;</span>}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

function InputArea({
  input,
  setInput,
  sendMessage,
  isProcessing = false,
  inputRef,
  voiceModeActive,
  setVoiceModeActive,
  onOpenCanvas,
  showCanvasAction = true,
  showCapabilityInfo = true,
}: {
  input: string;
  setInput: (v: string | ((prev: string) => string)) => void;
  sendMessage: () => void;
  isProcessing?: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  voiceModeActive: boolean;
  setVoiceModeActive: (v: boolean) => void;
  onOpenCanvas?: () => void;
  showCanvasAction?: boolean;
  showCapabilityInfo?: boolean;
}) {
  const navigate = useNavigate();

  const showWritingSuggestion = useMemo(() => {
    const lower = input.toLowerCase();
    if (lower.length < 8) return false;
    return WRITING_KEYWORDS.some(kw => lower.includes(kw));
  }, [input]);

  return (
    <div className="sticky bottom-0 z-20 shrink-0 w-full shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
      {showCanvasAction && (
        <div className="px-3 sm:px-4 pt-2 pb-0 bg-white dark:bg-gray-800 flex items-center gap-2 flex-wrap">
          <button
            onClick={() => onOpenCanvas ? onOpenCanvas() : navigate('/projects')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              showWritingSuggestion
                ? 'text-white bg-purple-600 dark:bg-purple-500 border border-purple-600 dark:border-purple-500 hover:bg-purple-700 dark:hover:bg-purple-600 animate-pulse'
                : 'text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 hover:bg-purple-100 dark:hover:bg-purple-900/50 hover:border-purple-300 dark:hover:border-purple-600'
            }`}
          >
            {showWritingSuggestion ? <Sparkles size={12} /> : <FileText size={12} />}
            {showWritingSuggestion ? 'Open Canvas for this!' : 'Open Canvas'}
          </button>
        </div>
      )}

      {voiceModeActive && (
        <div>
          <VoiceOverlay
            isOpen={true}
            onClose={() => setVoiceModeActive(false)}
            onDraftChange={(text) => {
              const trimmed = text.trim();
              if (!trimmed) return;
              setInput(prev => {
                const existing = prev.trim();
                return existing ? existing + ' ' + trimmed : trimmed;
              });
            }}
          />
        </div>
      )}
      <div className="px-3 py-3 sm:px-4 sm:py-4 bg-white dark:bg-gray-800 border-t border-light-border dark:border-gray-700 rounded-b-2xl">
        <div className="flex gap-2 items-end">
          {/* Capability info button — always visible for discoverability */}
          {showCapabilityInfo && <InfoButton />}
          {/* Voice Mode Toggle Button */}
          {setVoiceModeActive && (
            <button
              onClick={() => setVoiceModeActive(!voiceModeActive)}
              className={`px-3 py-2 min-h-[44px] min-w-[44px] rounded-lg ${
                voiceModeActive 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              } transition-colors flex items-center justify-center`}
              aria-label="Toggle voice mode"
              title="Voice Mode"
            >
              <Mic size={20} />
            </button>
          )}
          <textarea
            data-testid="message-input"
            ref={inputRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              const newHeight = Math.min(Math.max(e.target.scrollHeight, 56), 200);
              e.target.style.height = newHeight + 'px';
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !isProcessing) {
                e.preventDefault();
                sendMessage();
                (e.target as HTMLElement).style.height = '56px';
              }
            }}
            className="flex-1 resize-none overflow-auto border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 min-h-[56px] max-h-[200px]"
            placeholder="Type your message..."
            rows={2}
          />
          <button
            data-testid="send-message-btn"
            onClick={() => {
              if (isProcessing) return;
              sendMessage();
              if (inputRef.current) {
                inputRef.current.style.height = '56px';
              }
            }}
            className="px-6 py-3 min-h-[44px] min-w-[44px] rounded-full bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center font-medium"
            disabled={!input.trim() || isProcessing}
            aria-label="Send message"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

export default InputArea;
